import React, { useState, useEffect } from 'react';
import {
  Box, Typography, TextField, Collapse, Grid, Chip, Divider, Avatar
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import CalculateIcon from '@mui/icons-material/Calculate';
import axiosInstance from './axiosConfig';
import ModernLayout from './common/ModernLayout';
import ModernCard, { StatsCard } from './common/ModernCard';
import ModernButton, { GradientButton, GlassButton } from './common/ModernButton';

const Harvested = () => {
  const [harvests, setHarvests] = useState([]);
  const [expandedHarvestId, setExpandedHarvestId] = useState(null);

  useEffect(() => {
    fetchHarvests();
  }, []);

  const fetchHarvests = async () => {
    const projectId = localStorage.getItem('projectId');
    const response = await axiosInstance.get(`/api/projects/${projectId}/harvests`);
    setHarvests(response.data);
  };

  const handleToggleExpand = (harvestId) => {
    setExpandedHarvestId(expandedHarvestId === harvestId ? null : harvestId);
  };

  const handleVarietyChange = (harvestId, varietyIndex, field, value) => {
    setHarvests((prevHarvests) =>
      prevHarvests.map((harvest) =>
        harvest.id === harvestId
          ? {
              ...harvest,
              varieties: harvest.varieties.map((variety, index) =>
                index === varietyIndex ? { ...variety, [field]: value } : variety
              ),
            }
          : harvest
      )
    );
  };

  const handleRoomCostChange = (harvestId, value) => {
    setHarvests((prevHarvests) =>
      prevHarvests.map((harvest) =>
        harvest.id === harvestId ? { ...harvest, room_cost: parseFloat(value) || 0 } : harvest
      )
    );
  };

  const handleSaveHarvestData = async (harvestId) => {
    const harvest = harvests.find((h) => h.id === harvestId);

    const updatedVarieties = harvest.varieties.map((variety) => ({
      ...variety,
      total_value: (variety.dry_weight * variety.price_per_pound) / 453.592,
    }));

    const totalCycleValue = updatedVarieties.reduce(
      (acc, variety) => acc + variety.total_value,
      0
    ) - harvest.room_cost;

    await axiosInstance.put(`/api/harvests/${harvestId}`, {
      varieties: updatedVarieties,
      room_cost: harvest.room_cost,
      total_value: totalCycleValue,
    });

    fetchHarvests();
  };

  // Calculate total statistics
  const totalHarvests = harvests.length;
  const totalRevenue = harvests.reduce((acc, harvest) => {
    const harvestRevenue = harvest.varieties.reduce((sum, variety) => 
      sum + (variety.dry_weight * variety.price_per_pound), 0
    );
    return acc + harvestRevenue;
  }, 0);
  const totalCosts = harvests.reduce((acc, harvest) => acc + (harvest.room_cost || 0), 0);
  const totalProfit = totalRevenue - totalCosts;

  return (
    <ModernLayout title="Sistema de Presupuesto y Cosechas">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 1 }}>
          Sistema de Presupuesto y Cosechas
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Gestiona y analiza el rendimiento financiero de tus cultivos
        </Typography>
      </Box>

      {/* Stats Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Cosechas Totales"
            value={totalHarvests.toString()}
            subtitle="Ciclos completados"
            icon={<LocalShippingIcon />}
            color="primary"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Ingresos Totales"
            value={`$${totalRevenue.toFixed(2)}`}
            subtitle="Ventas acumuladas"
            icon={<MonetizationOnIcon />}
            color="success"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Costos Totales"
            value={`$${totalCosts.toFixed(2)}`}
            subtitle="Gastos operativos"
            icon={<CalculateIcon />}
            color="warning"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatsCard
            title="Beneficio Neto"
            value={`$${totalProfit.toFixed(2)}`}
            subtitle="Rentabilidad total"
            icon={<TrendingUpIcon />}
            color={totalProfit >= 0 ? "success" : "error"}
          />
        </Grid>
      </Grid>

      {/* Harvest Cycles */}
      {harvests.length === 0 ? (
        <ModernCard
          title="Sin Cosechas Registradas"
          subtitle="Aún no se ha producido ninguna recogida en los cuartos de este proyecto"
        >
          <Box
            sx={{
              p: 4,
              textAlign: 'center',
              borderRadius: 2,
              background: 'rgba(255, 255, 255, 0.05)',
              backdropFilter: 'blur(10px)',
              border: '1px solid rgba(255, 255, 255, 0.1)',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              Las cosechas aparecerán aquí una vez que completes ciclos de cultivo.
            </Typography>
          </Box>
        </ModernCard>
      ) : (
        <Grid container spacing={3}>
          {harvests.map((harvest) => (
            <Grid item xs={12} key={harvest.id}>
              <ModernCard
                title={`Cosecha: ${harvest.room_name}`}
                subtitle={`Ciclo desde ${harvest.cycle_start_date} hasta ${harvest.cycle_end_date || 'en curso'}`}
              >
                <Box sx={{ p: 2 }}>
                  {/* Harvest Summary */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 2,
                      p: 2,
                      borderRadius: 2,
                      background: 'rgba(255, 255, 255, 0.1)',
                      backdropFilter: 'blur(10px)',
                      border: '1px solid rgba(255, 255, 255, 0.2)',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        }}
                      >
                        <LocalShippingIcon />
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                          {harvest.room_name}
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          {harvest.varieties.length} variedades
                        </Typography>
                      </Box>
                    </Box>
                    <GlassButton
                      onClick={() => handleToggleExpand(harvest.id)}
                      startIcon={expandedHarvestId === harvest.id ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    >
                      {expandedHarvestId === harvest.id ? 'Ocultar' : 'Ver Detalles'}
                    </GlassButton>
                  </Box>

                  {/* Expanded Details */}
                  <Collapse in={expandedHarvestId === harvest.id} timeout="auto" unmountOnExit>
                    <Box sx={{ mt: 3 }}>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                        Detalles de Variedades
                      </Typography>
                      
                      <Grid container spacing={2}>
                        {harvest.varieties.map((variety, index) => (
                          <Grid item xs={12} md={6} key={index}>
                            <Box
                              sx={{
                                p: 2,
                                borderRadius: 2,
                                background: 'rgba(255, 255, 255, 0.05)',
                                backdropFilter: 'blur(10px)',
                                border: '1px solid rgba(255, 255, 255, 0.1)',
                              }}
                            >
                              <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2 }}>
                                {variety.variety || `Variedad ${index + 1}`}
                              </Typography>
                              
                              <Grid container spacing={2}>
                                <Grid item xs={6}>
                                  <TextField
                                    label="Número de Plantas"
                                    value={variety.count}
                                    disabled
                                    fullWidth
                                    size="small"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        background: 'rgba(255, 255, 255, 0.8)',
                                        backdropFilter: 'blur(10px)',
                                      },
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <TextField
                                    label="Peso Húmedo (lb)"
                                    type="number"
                                    value={variety.wet_weight || ''}
                                    onChange={(e) =>
                                      handleVarietyChange(harvest.id, index, 'wet_weight', e.target.value)
                                    }
                                    fullWidth
                                    size="small"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        background: 'rgba(255, 255, 255, 0.8)',
                                        backdropFilter: 'blur(10px)',
                                      },
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <TextField
                                    label="Peso Seco (lb)"
                                    type="number"
                                    value={variety.dry_weight || ''}
                                    onChange={(e) =>
                                      handleVarietyChange(harvest.id, index, 'dry_weight', e.target.value)
                                    }
                                    fullWidth
                                    size="small"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        background: 'rgba(255, 255, 255, 0.8)',
                                        backdropFilter: 'blur(10px)',
                                      },
                                    }}
                                  />
                                </Grid>
                                <Grid item xs={6}>
                                  <TextField
                                    label="Precio/lb ($)"
                                    type="number"
                                    value={variety.price_per_pound || ''}
                                    onChange={(e) =>
                                      handleVarietyChange(harvest.id, index, 'price_per_pound', e.target.value)
                                    }
                                    fullWidth
                                    size="small"
                                    sx={{
                                      '& .MuiOutlinedInput-root': {
                                        borderRadius: 2,
                                        background: 'rgba(255, 255, 255, 0.8)',
                                        backdropFilter: 'blur(10px)',
                                      },
                                    }}
                                  />
                                </Grid>
                              </Grid>
                              
                              <Box sx={{ mt: 2, p: 2, borderRadius: 2, background: 'rgba(255, 255, 255, 0.1)' }}>
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  Beneficio Estimado: $
                                  {((variety.dry_weight * variety.price_per_pound) || 0).toFixed(2)}
                                </Typography>
                              </Box>
                            </Box>
                          </Grid>
                        ))}
                      </Grid>

                      <Divider sx={{ my: 3 }} />

                      {/* Room Cost and Total Profit */}
                      <Box
                        sx={{
                          p: 3,
                          borderRadius: 2,
                          background: 'rgba(255, 255, 255, 0.1)',
                          backdropFilter: 'blur(10px)',
                          border: '1px solid rgba(255, 255, 255, 0.2)',
                        }}
                      >
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                          Análisis Financiero del Ciclo
                        </Typography>
                        
                        <Grid container spacing={3}>
                          <Grid item xs={12} md={6}>
                            <TextField
                              label="Costo del Room ($)"
                              type="number"
                              value={harvest.room_cost || ''}
                              onChange={(e) => handleRoomCostChange(harvest.id, e.target.value)}
                              fullWidth
                              sx={{
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  background: 'rgba(255, 255, 255, 0.8)',
                                  backdropFilter: 'blur(10px)',
                                },
                              }}
                            />
                          </Grid>
                          <Grid item xs={12} md={6}>
                            <Box sx={{ p: 2, borderRadius: 2, background: 'rgba(255, 255, 255, 0.1)' }}>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                Beneficio Total del Ciclo: $
                                {(harvest.varieties.reduce(
                                    (acc, v) => acc + (v.dry_weight * v.price_per_pound),
                                    0
                                ) - (harvest.room_cost || 0)).toFixed(2)}
                              </Typography>
                            </Box>
                          </Grid>
                        </Grid>

                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                          <GradientButton
                            onClick={() => handleSaveHarvestData(harvest.id)}
                            startIcon={<CalculateIcon />}
                          >
                            Guardar Datos de Cosecha
                          </GradientButton>
                        </Box>
                      </Box>
                    </Box>
                  </Collapse>
                </Box>
              </ModernCard>
            </Grid>
          ))}
        </Grid>
      )}
    </ModernLayout>
  );
};

export default Harvested;
